import React from 'react';
import { Dialog, CornerDialog, Pane, Heading, StatusIndicator, Badge } from 'evergreen-ui';
import { Tooltip as MatTooltip } from '@mui/material';
import { convertDate2 } from '../../helpers/dateConverter';
import { dateFilterActions } from '../../store/dateFilter';
import { isReconciled } from '../../helpers/reconciliationHelpers';
import EntryEditFormComponent from './EntryEditFormComponent';
import TransferEditFormComponent from './TransferEditFormComponent';
import EntryCopyFormComponent from './EntryCopyFormComponent';
import TransferCopyFormComponent from './TransferCopyFormComponent';
import InitialBalanceEditFormComponent from './InitialBalanceEditFormComponent';

export const EntriesTableDialogs = ({
  isShownDialog,
  setIsShownDialog,
  isShownCopyDialog,
  setIsShownCopyDialog,
  isShownTransferDialog,
  setIsShownTransferDialog,
  isShownInitialBalanceDialog,
  setIsShownInitialBalanceDialog,
  isShownTransferCopyDialog,
  setIsShownTransferCopyDialog,
  isShownDeleteDialog,
  setIsShownDeleteDialog,
  isShownDeleteManyDialog,
  setIsShowDeleteManyDialog,
  isShownCorner,
  setIsShownCorner,
  journalEntryOnFocus,
  journalEntriesFilteredByDate,
  setEntryJournalOnFocus,
  deleteMutation,
  deleteMany,
  expiredEntries,
  dispatch,
}) => {
  return (
    <>
      <EntryEditFormComponent
        isShown={isShownDialog}
        setIsShown={setIsShownDialog}
        journalEntry={journalEntryOnFocus}
      />
      <EntryCopyFormComponent
        isShown={isShownCopyDialog}
        setIsShown={setIsShownCopyDialog}
        journalEntry={journalEntryOnFocus}
      />
      <InitialBalanceEditFormComponent
        isShown={isShownInitialBalanceDialog}
        setIsShown={setIsShownInitialBalanceDialog}
        journalEntry={journalEntryOnFocus}
      />
      <TransferEditFormComponent
        isShown={isShownTransferDialog}
        setIsShown={setIsShownTransferDialog}
        journalEntry={journalEntryOnFocus}
        entry={journalEntriesFilteredByDate}
        setEntryJournalOnFocus={setEntryJournalOnFocus}
      />
      <TransferCopyFormComponent
        isShown={isShownTransferCopyDialog}
        setIsShown={setIsShownTransferCopyDialog}
        journalEntry={journalEntryOnFocus}
      />
      <Dialog
        isShown={isShownDeleteDialog}
        title="Deletar lançamento"
        intent="danger"
        onCloseComplete={() => setIsShownDeleteDialog(false)}
        confirmLabel="Deletar"
        cancelLabel="Cancelar"
        isConfirmDisabled={!!journalEntryOnFocus && isReconciled(journalEntryOnFocus)}
        onConfirm={() => deleteMutation(journalEntryOnFocus, setIsShownDeleteDialog)}
        preventBodyScrolling
      >
        {journalEntryOnFocus && isReconciled(journalEntryOnFocus) ? (
          <Pane padding={8} background="yellowTint" borderRadius={3}>
            <StatusIndicator color="warning">
              ⚠️ Este lançamento está conciliado e não pode ser excluído.
            </StatusIndicator>
          </Pane>
        ) : (
          'Tem certeza que deseja deletar este lançamento?'
        )}
      </Dialog>
      <Dialog
        isShown={isShownDeleteManyDialog}
        title="Deletar lançamentos"
        intent="danger"
        onCloseComplete={() => setIsShowDeleteManyDialog(false)}
        confirmLabel="Deletar"
        cancelLabel="Cancelar"
        isConfirmDisabled={
          Array.isArray(journalEntryOnFocus) &&
          journalEntryOnFocus.some(
            (entry) => entry.journalEntryType === 2 || isReconciled(entry)
          )
        }
        onConfirm={() => deleteMany(journalEntryOnFocus, setIsShowDeleteManyDialog)}
        preventBodyScrolling
      >
        <Pane>
          <Pane marginBottom={16}>
            Tem certeza que deseja deletar{' '}
            {Array.isArray(journalEntryOnFocus) ? journalEntryOnFocus.length : 0} lançamento(s)
            selecionado(s)?
          </Pane>
          {Array.isArray(journalEntryOnFocus) && (
            <Pane>
              {journalEntryOnFocus.some((entry) => entry.journalEntryType === 2) && (
                <Pane marginBottom={8} padding={8} background="yellowTint" borderRadius={3}>
                  <StatusIndicator color="warning">
                    ⚠️ Há lançamentos de saldo inicial na seleção — remova-os para poder excluir os demais
                  </StatusIndicator>
                </Pane>
              )}
              {journalEntryOnFocus.some((entry) => isReconciled(entry)) && (
                <Pane marginBottom={8} padding={8} background="yellowTint" borderRadius={3}>
                  <StatusIndicator color="warning">
                    ⚠️ Há lançamentos conciliados na seleção — remova-os para poder excluir os demais
                  </StatusIndicator>
                </Pane>
              )}
              {journalEntryOnFocus.some((entry) => entry.journalEntryType === 5) && (
                <Pane marginBottom={8} padding={8} background="blueTint" borderRadius={3}>
                  <StatusIndicator color="info">
                    ℹ️ Transferências com pelo menos uma das partes conciliadas não podem ser
                    excluídas
                  </StatusIndicator>
                </Pane>
              )}
            </Pane>
          )}
        </Pane>
      </Dialog>

      <CornerDialog
        className="cornerDialog"
        title="Lançamentos vencidos:"
        isShown={isShownCorner}
        onCloseComplete={() => setIsShownCorner(false)}
        hasFooter={false}
        width="290px"
      >
        <Pane className="cornerDialogPane">
          <Pane overflowY="auto">
            {expiredEntries
              .sort((a, b) => {
                const nameA = a.dateVencimento.toUpperCase();
                const nameB = b.dateVencimento.toUpperCase();

                if (nameA > nameB) {
                  return -1;
                }

                if (nameA < nameB) {
                  return 1;
                }

                return 0;
              })
              .map((entry) => {
                return (
                  <Pane key={entry.id} display="flex" gap={12} margin={4} alignItems="center">
                    <Badge color={entry.isInput ? 'blue' : 'red'}>
                      {entry.isInput ? 'Entrada' : 'Saída'}
                    </Badge>
                    <MatTooltip title="Filtrar" arrow placement="right" enterDelay={500}>
                      <Heading
                        className="expiredDate"
                        size={400}
                        onClick={() => {
                          dispatch(
                            dateFilterActions.setStartDate(entry.dateVencimento.substr(0, 10))
                          );
                          dispatch(
                            dateFilterActions.setStartDateTemp(entry.dateVencimento.substr(0, 10))
                          );
                          dispatch(
                            dateFilterActions.setEndDate(entry.dateVencimento.substr(0, 10))
                          );
                          dispatch(
                            dateFilterActions.setEndDateTemp(entry.dateVencimento.substr(0, 10))
                          );
                        }}
                        cursor="pointer"
                      >
                        {convertDate2(entry.dateVencimento)}
                      </Heading>
                    </MatTooltip>
                  </Pane>
                );
              })}
          </Pane>
        </Pane>
      </CornerDialog>
      <Heading
        display="flex"
        size={400}
        margin="auto"
        textAlign="center"
        color="red"
        onClick={() => setIsShownCorner(true)}
        cursor="pointer"
        width="fit-content"
      >
        {expiredEntries.length > 1 ? (
          <Pane display="flex" position="relative">
            <StatusIndicator
              color="danger"
              marginRight={8}
            >{`Você possui ${expiredEntries.length} lançamentos vencidos neste período`}</StatusIndicator>
          </Pane>
        ) : expiredEntries.length === 1 ? (
          <Pane display="flex" position="relative">
            <StatusIndicator color="danger" marginRight={8}>
              Você possui 1 lançamento vencido neste período
            </StatusIndicator>
          </Pane>
        ) : null}
      </Heading>
    </>
  );
};
